import { DateTime, Interval } from 'luxon';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { startEndTime } from 'dashboard/lib/workflows/run-stats';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStep, WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import {
  WorkflowStep,
  WorkflowStepInput,
  WorkflowStepOutput,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import WorkflowRunBlockTimeline from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunBlockTimeline';
import Table, { TableBody, TableHead, TableHeaderCell } from 'design-system/components/Table';
import { TokensToNode } from 'lib/workflows';

export const DURATION_COLUMN_WIDTH = 100;
const DIVIDER_COLUMN_WIDTH = 4;

// Provide the parent component's x and width to use during resize operations
const ViewPortMarker = styled.div`
  width: 100%;
  height: 0;
  position: sticky;
  left: 0;
`;

const FixedTable = styled(Table).attrs<{ mode: TimelineViewMode; totalWidth: number | null }>(
  ({ mode, totalWidth }) => ({
    style: {
      width: mode === 'fit' || !totalWidth ? '100%' : `${totalWidth}px`,
    },
  }),
)<{ mode: TimelineViewMode; totalWidth: number | null }>`
  table-layout: fixed;
`;

const StyledTableHead = styled(TableHead)`
  z-index: 2;
`;

const FlexibleTableHeaderCell = styled(TableHeaderCell).attrs<{
  customWidth: number;
}>(({ customWidth }) => ({
  style: {
    width: `${customWidth}px`,
  },
}))<{
  customWidth: number;
}>`
  width: 20%;
  overflow: hidden;
  text-overflow: ellipsis;

  position: sticky;
  left: 0;
  z-index: 2;
`;

const FixedTableHeaderCell = styled(TableHeaderCell).attrs<{
  customWidth: number;
  fixedOffset: number;
  includePadding: boolean;
  includeGridline: boolean;
}>(({ fixedOffset }) => ({
  style: {
    left: `${fixedOffset}px`,
  },
}))<{
  customWidth: number;
  fixedOffset: number;
  includePadding: boolean;
  includeGridline: boolean;
}>`
  width: ${({ customWidth }) => customWidth}px;

  ${({ includePadding }) =>
    !includePadding &&
    `
    padding: 0;
  `}

  ${({ includeGridline, theme }) =>
    includeGridline &&
    `
    border-right: 1px solid ${theme.color.border.primary};
  `}
  position: sticky;
  z-index: 2;
`;

export type TimelineViewMode = 'fit' | 'expanded';

export interface CommonTimelineProps {
  tokensToSteps: TokensToNode;
  tokensToRunSteps: Record<string, WorkflowRunStep>;
  allRunSteps: WorkflowRunStep[];
  searchString: string;
  activeNode: WorkflowStepInput | WorkflowStepOutput | WorkflowStep | null;
  viewMode: TimelineViewMode;
  selectNode: (token: string | null) => void;
  updateLoopCycle: (loopCycle: Record<string, number>) => void;
}

interface WorkflowRunTimelineProps {
  workflowVersion: WorkflowVersion;
  workflowRun: WorkflowRun;
  allowSetViewMode: (allow: boolean) => void;
}

const WorkflowRunTimeline: FC<WorkflowRunTimelineProps & CommonTimelineProps> = ({
  workflowVersion,
  workflowRun,
  tokensToSteps,
  tokensToRunSteps,
  allRunSteps,
  searchString = '',
  viewMode = 'fit',
  activeNode,
  selectNode,
  updateLoopCycle,
  allowSetViewMode,
}) => {
  const [isResizingColumns, setIsResizingColumns] = useState(false);
  const [flexibleColumnWidth, setFlexibleColumnWidth] = useState(300);
  const [tableWidth, setTableWidth] = useState<number | null>(null);

  const viewPortMarkerRef = useRef<HTMLDivElement>(null);

  const topLevelSteps = useMemo(() => {
    return workflowVersion.attributes['steps-config']
      .map((configStep) => tokensToRunSteps[configStep.token])
      .filter((step) => step && step.attributes.status !== WorkflowRunStepStatus.Canceled);
  }, [workflowVersion, tokensToRunSteps]);

  const totalInterval = useMemo(() => {
    if (topLevelSteps.length === 0) {
      return Interval.invalid('no steps found');
    }
    const firstStep = topLevelSteps[0];
    const lastStep = topLevelSteps[topLevelSteps.length - 1];

    return Interval.fromDateTimes(
      DateTime.fromISO(firstStep.attributes['enqueued-at'] || firstStep.attributes['created-at']),
      DateTime.fromISO(lastStep.attributes['completed-at'] || lastStep.attributes['updated-at']),
    );
  }, [topLevelSteps]);

  const minimumLegibleTimelineWidth = useMemo(() => {
    const runTimesInMs = allRunSteps
      .filter((step) => step && step.attributes.status !== WorkflowRunStepStatus.Canceled)
      .map((step) => {
        const { startTime, endTime } = startEndTime([step]);
        return endTime.diff(startTime).as('milliseconds');
      });

    const maxRunTime = Math.max(...runTimesInMs);
    const minRunTime = Math.min(...runTimesInMs);

    // Guarantees the smallest span will show up as 4px wide.
    return (maxRunTime / minRunTime) * 4;
  }, [allRunSteps]);

  const calculateTableWidth = useCallback(() => {
    if (!viewPortMarkerRef.current) {
      allowSetViewMode(false);
      return;
    }

    const parentWidth = viewPortMarkerRef.current.getBoundingClientRect().width;

    const minimumLegibleTableWidth =
      minimumLegibleTimelineWidth +
      flexibleColumnWidth +
      DURATION_COLUMN_WIDTH +
      DIVIDER_COLUMN_WIDTH;

    setTableWidth(viewMode === 'fit' ? null : Math.max(parentWidth, minimumLegibleTableWidth));
    allowSetViewMode(parentWidth < minimumLegibleTableWidth);
  }, [minimumLegibleTimelineWidth, flexibleColumnWidth, viewMode, allowSetViewMode]);

  useEffect(() => {
    calculateTableWidth();

    const resizeFixedColumns = (event: MouseEvent) => {
      if (isResizingColumns && viewPortMarkerRef.current) {
        setFlexibleColumnWidth(
          event.clientX -
            viewPortMarkerRef.current.getBoundingClientRect().x -
            DURATION_COLUMN_WIDTH,
        );

        calculateTableWidth();
      }
      event.preventDefault(); // prevent text selection
    };

    const stopDragging = () => {
      setIsResizingColumns(false);
    };

    window.addEventListener('resize', calculateTableWidth);
    document.addEventListener('mousemove', resizeFixedColumns);
    document.addEventListener('mouseup', stopDragging);
    return () => {
      window.removeEventListener('resize', calculateTableWidth);
      document.removeEventListener('mousemove', resizeFixedColumns);
      document.removeEventListener('mouseup', stopDragging);
    };
  });

  return (
    <>
      <FixedTable mode={viewMode} totalWidth={tableWidth}>
        <StyledTableHead sticky>
          <FlexibleTableHeaderCell customWidth={flexibleColumnWidth}>
            Step name
          </FlexibleTableHeaderCell>
          <FixedTableHeaderCell
            fixedOffset={flexibleColumnWidth}
            customWidth={DURATION_COLUMN_WIDTH}
            includePadding={true}
            includeGridline={false}
          >
            Duration
          </FixedTableHeaderCell>
          <FixedTableHeaderCell
            fixedOffset={flexibleColumnWidth + DURATION_COLUMN_WIDTH}
            customWidth={DIVIDER_COLUMN_WIDTH}
            includePadding={false}
            includeGridline={true}
          ></FixedTableHeaderCell>
          <TableHeaderCell></TableHeaderCell>
        </StyledTableHead>
        <TableBody>
          {topLevelSteps.length > 0 && (
            <WorkflowRunBlockTimeline
              tokensToSteps={tokensToSteps}
              tokensToRunSteps={tokensToRunSteps}
              allRunSteps={allRunSteps}
              steps={topLevelSteps}
              id={workflowRun.id}
              totalInterval={totalInterval}
              flexibleColumnWidth={flexibleColumnWidth}
              searchString={searchString}
              viewMode={viewMode}
              activeNode={activeNode}
              selectNode={selectNode}
              setIsResizingColumns={setIsResizingColumns}
              updateLoopCycle={updateLoopCycle}
            />
          )}
        </TableBody>
      </FixedTable>
      <ViewPortMarker ref={viewPortMarkerRef} />
    </>
  );
};

export default WorkflowRunTimeline;
